import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Select, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import { DownOutlined, PlusOutlined, UndoOutlined, UploadOutlined, UpOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';

import { ButtonLinkPreviewFAI } from '@partsbadger/library';
import { ICustomRequirements, IRequirement } from '@partsbadger/types';
import { API_ROOT, authenticatedUploadProps } from '@partsbadger/utils';

import QuoteStore from '../../stores/QuoteStore';
import {
    addRequirement,
    classifyQuoteRequirements,
    describeRequirementDetails,
    getRequirementChangeState,
    listAddableRequirements,
    listPendingRemovals,
    listQuickAddRequirements,
    orderLikeSaved,
    restoreDefaultRequirement,
    undoRequirementRemoval,
    updateRequirementDetails,
} from '../../utils/quoteRequirements';
import type { QuoteRequirementsDraft } from './useQuoteRequirementsDraft';

const FILE_URL = `${API_ROOT}/customer/files/`;

interface Props {
    draft: QuoteRequirementsDraft;
    showIntro?: boolean;
}

/** Keeps a value registered on the parent Form (checkout reads it on submit) without rendering a control. */
const FormValue = (_: { value?: unknown }) => null;

const needsDetails = (fieldType: string | undefined) =>
    fieldType === 'input' || fieldType === 'inputtext' || fieldType === 'file';

type ChangeState = 'new' | 'edited' | 'removed';

const CHANGE_LABEL: Record<ChangeState, string> = { new: 'Added', edited: 'Updated', removed: 'To be removed' };

const PendingLabel = ({ state }: { state: ChangeState }) => (
    <span className="quote-requirements-pending-label">{CHANGE_LABEL[state]}</span>
);

type DetailsChange = Partial<Pick<ICustomRequirements, 'value' | 'files'>>;

interface RequirementDetailsProps {
    requirement: ICustomRequirements;
    fieldType: string | undefined;
    disabled: boolean;
    onChange: (details: DetailsChange) => void;
    onUploadingChange: (uploading: boolean) => void;
}

const RequirementDetails = ({
    requirement,
    fieldType,
    disabled,
    onChange,
    onUploadingChange,
}: RequirementDetailsProps) => {
    const inputId = `quote-requirement-value-${requirement.key}`;

    if (fieldType === 'file') {
        const defaultFileList: UploadFile[] = (requirement.files ?? []).map(file => ({
            uid: String(file.id),
            name: file.filename,
            status: 'done',
            url: file.file,
            response: file,
        }));

        return (
            <Upload
                {...authenticatedUploadProps(FILE_URL)}
                defaultFileList={defaultFileList}
                disabled={disabled}
                multiple
                name="file"
                onChange={({ fileList }) => {
                    // Files upload right away; they are attached to the quote when the draft is applied.
                    const uploading = fileList.some(file => file.status === 'uploading');
                    onUploadingChange(uploading);
                    if (uploading) return;

                    onChange({
                        files: fileList
                            .filter(file => file.status === 'done' && file.response?.id)
                            .map(file => ({
                                id: file.response.id,
                                filename: file.response.filename,
                                file: file.response.file,
                            })),
                    });
                }}
            >
                <Button className="quote-requirements-upload-button" disabled={disabled} icon={<UploadOutlined />}>
                    Upload files
                </Button>
            </Upload>
        );
    }

    const InputControl = fieldType === 'inputtext' ? Input.TextArea : Input;

    return (
        <div className="quote-requirements-value">
            <label htmlFor={inputId}>Value</label>
            <InputControl
                disabled={disabled}
                id={inputId}
                onChange={event => onChange({ value: event.target.value })}
                placeholder={`Value for ${requirement.label}`}
                value={String(requirement.value ?? '')}
            />
        </div>
    );
};

interface RequirementRowProps {
    requirement: ICustomRequirements;
    fieldType: string | undefined;
    changeState: ChangeState | null;
    group: 'defaults' | 'additions';
    expanded: boolean;
    disabled: boolean;
    onToggle: () => void;
    onRemove: () => void;
    onUndo: () => void;
    onChangeDetails: (details: DetailsChange) => void;
    onUploadingChange: (uploading: boolean) => void;
}

const RequirementRow = ({
    requirement,
    fieldType,
    changeState,
    group,
    expanded,
    disabled,
    onToggle,
    onRemove,
    onUndo,
    onChangeDetails,
    onUploadingChange,
}: RequirementRowProps) => {
    const summary = describeRequirementDetails(requirement, fieldType);
    const detailsId = `quote-requirement-details-${requirement.key}`;
    const removing = changeState === 'removed';

    return (
        <li
            className={`quote-requirements-row${changeState ? ' is-pending' : ''}${removing ? ' is-removing' : ''}`}
            data-change-state={changeState ?? 'none'}
            data-requirement-group={group}
            data-testid={`quote-requirement-${requirement.key}`}
        >
            <div className="quote-requirements-row-main">
                <div className="quote-requirements-row-text">
                    <span className="quote-requirements-row-name">{requirement.label}</span>
                    {summary ? <span className="quote-requirements-row-summary">{summary}</span> : null}
                    {changeState ? <PendingLabel state={changeState} /> : null}
                </div>
                <div className="quote-requirements-row-actions">
                    {removing ? (
                        <Button
                            aria-label={`Undo removal of ${requirement.label}`}
                            data-testid="quote-requirement-undo"
                            disabled={disabled}
                            icon={<UndoOutlined />}
                            onClick={onUndo}
                        >
                            Undo
                        </Button>
                    ) : null}
                    {!removing && needsDetails(fieldType) ? (
                        <Button
                            aria-controls={detailsId}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Edit'} details for ${requirement.label}`}
                            data-testid="quote-requirement-toggle"
                            icon={expanded ? <UpOutlined /> : <DownOutlined />}
                            onClick={onToggle}
                            type="text"
                        >
                            {expanded ? 'Hide' : 'Edit'}
                        </Button>
                    ) : null}
                    {!removing ? (
                        <Button
                            aria-label={`Remove ${requirement.label}`}
                            data-testid="quote-requirement-remove"
                            disabled={disabled}
                            onClick={onRemove}
                            type="text"
                        >
                            Remove
                        </Button>
                    ) : null}
                </div>
            </div>
            {!removing && needsDetails(fieldType) && expanded ? (
                <div className="quote-requirements-row-details" id={detailsId}>
                    <RequirementDetails
                        disabled={disabled}
                        fieldType={fieldType}
                        onChange={onChangeDetails}
                        onUploadingChange={onUploadingChange}
                        requirement={requirement}
                    />
                </div>
            ) : null}
        </li>
    );
};

export const QuoteRequirementsEditor = observer(({ draft, showIntro = true }: Props) => {
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const [uploadingKeys, setUploadingKeys] = useState<string[]>([]);
    const [searchResets, setSearchResets] = useState(0);
    const pendingChanges = useRef(0);
    pendingChanges.current = draft.changes.count;

    const { requirements, savedRequirements, applying: disabled, change, setUploading, version } = draft;

    useEffect(() => setUploading(uploadingKeys.length > 0), [uploadingKeys, setUploading]);
    useEffect(() => setUploadingKeys([]), [version]);

    const quote = QuoteStore.quote;
    const isHobbyist = !!quote?.contact?.is_hobbyist;
    const catalog = QuoteStore.additional_requirements;
    const defaults = QuoteStore.default_requirements;
    const { customerDefaults, quoteAdditions, missingDefaults } = classifyQuoteRequirements(requirements, defaults);
    const addable = listAddableRequirements(catalog, requirements, defaults);
    const quickAdds = listQuickAddRequirements(catalog, requirements, defaults, {
        materialAndDomestic: !isHobbyist,
        files2d: !!QuoteStore.needs2DFiles(),
        files3d: !!QuoteStore.needs3DFiles(),
    });
    const savedByKey = new Map(savedRequirements.map(requirement => [String(requirement.key), requirement]));
    const pendingRemovals = listPendingRemovals(savedRequirements, requirements, defaults);
    const pendingRemovalKeys = new Set(pendingRemovals.map(requirement => String(requirement.key)));

    // A pending removal is undone with the saved copy, so its value and files come back unchanged.
    const undoRemoval = (key: string | number) => {
        const saved = savedByKey.get(String(key));
        if (saved) change(undoRequirementRemoval(saved, savedRequirements, requirements));
    };

    const fieldTypeOf = (requirement: ICustomRequirements) =>
        catalog.find(item => String(item.id) === String(requirement.key))?.field_type;

    const add = (requirement: IRequirement) => {
        change(addRequirement(requirement, requirements, defaults));
        if (needsDetails(requirement.field_type)) {
            setExpandedKeys(keys => [...keys, String(requirement.id)]);
        }
    };

    const toggleDetails = (key: string) =>
        setExpandedKeys(keys => (keys.includes(key) ? keys.filter(item => item !== key) : [...keys, key]));

    const setRowUploading = (key: string, uploading: boolean) =>
        setUploadingKeys(keys => {
            const others = keys.filter(item => item !== key);
            return uploading ? [...others, key] : others;
        });

    const renderGroup = (title: string, group: ICustomRequirements[], testId: string) =>
        group.length ? (
            <section aria-label={title} className="quote-requirements-group" data-testid={testId}>
                <h3 className="quote-requirements-group-title">
                    {title} <span className="quote-requirements-group-count">· {group.length}</span>
                </h3>
                <ul className="quote-requirements-rows">
                    {orderLikeSaved(group, savedRequirements).map(requirement => {
                        const key = String(requirement.key);
                        return (
                            <RequirementRow
                                changeState={
                                    pendingRemovalKeys.has(key)
                                        ? 'removed'
                                        : getRequirementChangeState(requirement, savedRequirements)
                                }
                                disabled={disabled}
                                expanded={expandedKeys.includes(key)}
                                fieldType={fieldTypeOf(requirement)}
                                group={testId === 'quote-requirements-defaults' ? 'defaults' : 'additions'}
                                key={`${version}-${key}`}
                                onChangeDetails={details =>
                                    change(updateRequirementDetails(requirements, requirement.key, details))
                                }
                                onRemove={() => {
                                    change(requirements.filter(item => String(item.key) !== key));
                                    setRowUploading(key, false);
                                }}
                                onToggle={() => toggleDetails(key)}
                                onUndo={() => undoRemoval(key)}
                                onUploadingChange={uploading => setRowUploading(key, uploading)}
                                requirement={requirement}
                            />
                        );
                    })}
                </ul>
            </section>
        ) : null;

    const addableByType = addable.reduce<Record<string, IRequirement[]>>((groups, requirement) => {
        const type = requirement.type || 'Other';
        groups[type] = [...(groups[type] ?? []), requirement];
        return groups;
    }, {});

    return (
        <div aria-busy={disabled} className="quote-requirements-editor" data-testid="quote-requirements-editor">
            <Form.Item
                hidden
                initialValue={quote?.custom_requirements ?? []}
                name="additional_requirements"
                noStyle
                rules={[
                    {
                        // Checkout submits what is saved on the quote, so a pending draft would be lost.
                        validator: () =>
                            pendingChanges.current
                                ? Promise.reject(
                                      new Error('Save or discard your requirement changes before submitting.')
                                  )
                                : Promise.resolve(),
                    },
                ]}
            >
                <FormValue />
            </Form.Item>
            {!isHobbyist ? (
                <>
                    <Form.Item
                        hidden
                        initialValue={quote?.customer_supplied_material}
                        name="customer_supplied_material"
                        noStyle
                    >
                        <FormValue />
                    </Form.Item>
                    <Form.Item hidden initialValue={quote?.domestic_quoting} name="domestic_quoting" noStyle>
                        <FormValue />
                    </Form.Item>
                </>
            ) : null}
            {QuoteStore.needs2DFiles() ? (
                <Form.Item hidden initialValue={quote?.create_2d_files} name="create_2d_files" noStyle>
                    <FormValue />
                </Form.Item>
            ) : null}
            {QuoteStore.needs3DFiles() ? (
                <Form.Item hidden initialValue={quote?.create_3d_files} name="create_3d_files" noStyle>
                    <FormValue />
                </Form.Item>
            ) : null}

            {showIntro ? (
                <Typography.Paragraph className="quote-requirements-intro" type="secondary">
                    Requirements apply to the whole quote. Saving recalculates prices.
                </Typography.Paragraph>
            ) : null}

            {requirements.length || pendingRemovals.length ? (
                <>
                    {renderGroup('Customer defaults', customerDefaults, 'quote-requirements-defaults')}
                    {renderGroup(
                        'Added to this quote',
                        [...quoteAdditions, ...pendingRemovals],
                        'quote-requirements-additions'
                    )}
                </>
            ) : (
                <p className="quote-requirements-empty">No requirements on this quote.</p>
            )}

            {missingDefaults.length ? (
                <section
                    aria-label="Customer defaults not included"
                    className="quote-requirements-group quote-requirements-removed"
                    data-testid="quote-requirements-missing-defaults"
                >
                    <h3 className="quote-requirements-group-title">
                        Customer defaults not included{' '}
                        <span className="quote-requirements-group-count">· {missingDefaults.length}</span>
                    </h3>
                    <ul className="quote-requirements-rows">
                        {missingDefaults.map(requirement => {
                            const removalPending = savedByKey.has(String(requirement.key));
                            return (
                                <li
                                    className={`quote-requirements-row${
                                        removalPending ? ' is-pending is-removing' : ''
                                    }`}
                                    key={String(requirement.key)}
                                >
                                    <div className="quote-requirements-row-main">
                                        <div className="quote-requirements-row-text">
                                            <span className="quote-requirements-row-name">{requirement.label}</span>
                                            <span className="quote-requirements-row-summary">
                                                {removalPending
                                                    ? 'Customer default'
                                                    : 'Customer default removed from this quote'}
                                            </span>
                                            {removalPending ? <PendingLabel state="removed" /> : null}
                                        </div>
                                        <div className="quote-requirements-row-actions">
                                            <Button
                                                aria-label={`${removalPending ? 'Undo removal of' : 'Restore'} ${
                                                    requirement.label
                                                }`}
                                                disabled={disabled}
                                                icon={<UndoOutlined />}
                                                onClick={() =>
                                                    removalPending
                                                        ? undoRemoval(requirement.key)
                                                        : change(restoreDefaultRequirement(requirement, requirements))
                                                }
                                            >
                                                {removalPending ? 'Undo' : 'Restore'}
                                            </Button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}

            <section aria-labelledby="quote-requirements-add-label" className="quote-requirements-add">
                <label
                    className="quote-requirements-group-title"
                    htmlFor="quote-requirements-search"
                    id="quote-requirements-add-label"
                >
                    Add or edit requirements
                </label>
                <Select<number>
                    // Remounted after each pick so the search clears; focus stays here for the next one.
                    autoFocus={searchResets > 0}
                    className="quote-requirements-search"
                    data-testid="quote-requirements-add"
                    disabled={disabled}
                    id="quote-requirements-search"
                    key={searchResets}
                    notFoundContent="No more requirements to add"
                    onSelect={(id: number) => {
                        const requirement = addable.find(item => item.id === id);
                        if (requirement) add(requirement);
                        setSearchResets(count => count + 1);
                    }}
                    optionFilterProp="children"
                    placeholder="Search requirements to add"
                    showSearch
                >
                    {Object.entries(addableByType).map(([type, group]) => (
                        <Select.OptGroup key={type} label={type}>
                            {group.map(requirement => (
                                <Select.Option key={requirement.id} value={requirement.id}>
                                    {requirement.name}
                                </Select.Option>
                            ))}
                        </Select.OptGroup>
                    ))}
                </Select>
                {quickAdds.length ? (
                    <div aria-label="Common requirements" className="quote-requirements-quick-adds" role="group">
                        {quickAdds.map(({ flag, requirement }) => (
                            <Button
                                aria-label={`Add ${requirement.name}`}
                                className="quote-requirements-quick-add"
                                data-testid={`quote-requirements-quick-add-${flag}`}
                                disabled={disabled}
                                icon={<PlusOutlined />}
                                key={flag}
                                onClick={() => add(requirement)}
                            >
                                {requirement.name}
                            </Button>
                        ))}
                    </div>
                ) : null}
            </section>

            {requirements.length ? <ButtonLinkPreviewFAI custom_requirements={requirements} /> : null}
        </div>
    );
});

export default QuoteRequirementsEditor;
